/**
 * Pure utility functions for the Parenting Time Calendar feature.
 * All functions are stateless and independently testable.
 */

export type OvernightOption =
  | "sunday"
  | "monday"
  | "tuesday"
  | "wednesday"
  | "thursday"
  | "friday"
  | "saturday"
  | "weekend"
  | "weekdays";

export interface PatternConfig {
  frequency: "every" | "everyOther";
  overnights: OvernightOption;
  startDate: string; // ISO date string YYYY-MM-DD
}

/**
 * Returns an array of ISO date strings for a date range starting from startDate.
 * @param startDate - ISO date string (YYYY-MM-DD)
 * @param years - Number of years for the range (1 or 2). Defaults to 2.
 * The range is exactly N years: e.g., 2025-01-15 to 2027-01-14 (inclusive) for 2 years.
 */
export function generateDateRange(startDate: string, years: 1 | 2 = 2): string[] {
  const start = new Date(startDate + "T00:00:00");
  const endDate = new Date(startDate + "T00:00:00");
  endDate.setFullYear(endDate.getFullYear() + years);
  // end is exclusive — N years later minus one day is the last included date

  const dates: string[] = [];
  const current = new Date(start);

  while (current < endDate) {
    dates.push(formatDate(current));
    current.setDate(current.getDate() + 1);
  }

  return dates;
}

/**
 * Returns a holiday map for the given years.
 * Keys are ISO date strings, values are holiday names.
 * Includes 10 holidays per year as required by Georgia state holidays.
 */
export function getGeorgiaHolidays(
  years: number[]
): Record<string, string> {
  const holidays: Record<string, string> = {};

  for (const year of years) {
    // Fixed-date holidays
    holidays[`${year}-01-01`] = "New Year's Day";
    holidays[`${year}-07-04`] = "Independence Day";
    holidays[`${year}-12-25`] = "Christmas Day";

    // Martin Luther King Jr. Day — 3rd Monday of January
    holidays[formatDate(getNthDayOfMonth(year, 0, 1, 3))] =
      "Martin Luther King Jr. Day";

    // Mother's Day — 2nd Sunday of May
    holidays[formatDate(getNthDayOfMonth(year, 4, 0, 2))] = "Mother's Day";

    // Memorial Day — last Monday of May
    holidays[formatDate(getLastDayOfMonth(year, 4, 1))] = "Memorial Day";

    // Father's Day — 3rd Sunday of June
    holidays[formatDate(getNthDayOfMonth(year, 5, 0, 3))] = "Father's Day";

    // Labor Day — 1st Monday of September
    holidays[formatDate(getNthDayOfMonth(year, 8, 1, 1))] = "Labor Day";

    // Thanksgiving Day — 4th Thursday of November
    holidays[formatDate(getNthDayOfMonth(year, 10, 4, 4))] =
      "Thanksgiving Day";

    // Day after Thanksgiving — 4th Friday of November
    holidays[formatDate(getNthDayOfMonth(year, 10, 5, 4))] =
      "Day after Thanksgiving";
  }

  return holidays;
}

/**
 * Fills dates per pattern for the active parent across the entire date range.
 * For "every" frequency, assigns all dates matching the overnight option.
 * For "everyOther" frequency, alternates weeks starting from the pattern's startDate week.
 * Dates already assigned to the other parent are overwritten.
 */
export function populatePattern(
  config: PatternConfig,
  dateRange: string[],
  existingData: Record<string, string>,
  activeParent: string
): Record<string, string> {
  const result = { ...existingData };
  const targetDays = getTargetDaysOfWeek(config.overnights);
  const patternStartDate = new Date(config.startDate + "T00:00:00");

  for (const dateStr of dateRange) {
    const date = new Date(dateStr + "T00:00:00");
    const dayOfWeek = date.getDay(); // 0=Sun, 6=Sat

    if (!targetDays.includes(dayOfWeek)) {
      continue;
    }

    if (config.frequency === "every") {
      result[dateStr] = activeParent;
    } else {
      // "everyOther" — check if this date's week matches the pattern
      if (isMatchingWeek(patternStartDate, date)) {
        result[dateStr] = activeParent;
      }
    }
  }

  return result;
}

/**
 * Toggle/assign/reassign logic for clicking a date cell.
 * - Unassigned → assign to active parent
 * - Assigned to active parent → unassign
 * - Assigned to other parent → reassign to active parent
 * All other dates remain unchanged.
 */
export function handleDateClick(
  calendarData: Record<string, string>,
  dateStr: string,
  activeParent: string
): Record<string, string> {
  const result = { ...calendarData };
  const current = result[dateStr];

  if (!current) {
    // Unassigned → assign to active parent
    result[dateStr] = activeParent;
  } else if (current === activeParent) {
    // Assigned to active parent → unassign
    delete result[dateStr];
  } else {
    // Assigned to other parent → reassign to active parent
    result[dateStr] = activeParent;
  }

  return result;
}

/**
 * Count overnight entries per parent value.
 * If dateRange is provided, only counts dates within that range.
 */
export function countOvernights(
  calendarData: Record<string, string>,
  dateRange?: string[]
): { parent1: number; parent2: number } {
  let parent1 = 0;
  let parent2 = 0;

  if (dateRange) {
    const rangeSet = new Set(dateRange);
    for (const [key, value] of Object.entries(calendarData)) {
      if (!rangeSet.has(key)) continue;
      if (value === "parent1") {
        parent1++;
      } else if (value === "parent2") {
        parent2++;
      }
    }
  } else {
    for (const value of Object.values(calendarData)) {
      if (value === "parent1") {
        parent1++;
      } else if (value === "parent2") {
        parent2++;
      }
    }
  }

  return { parent1, parent2 };
}

/**
 * Build an accessible ARIA label for a date cell.
 * Contains formatted date, assignment status description, and holiday name if present.
 */
export function buildAriaLabel(
  dateStr: string,
  assignment: string | null,
  holidayName: string | null,
  parentName1: string,
  parentName2: string
): string {
  const date = new Date(dateStr + "T00:00:00");
  const formattedDate = date.toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  let status: string;
  if (!assignment) {
    status = "Unassigned";
  } else if (assignment === "parent1") {
    status = `Assigned to ${parentName1}`;
  } else {
    status = `Assigned to ${parentName2}`;
  }

  let label = `${formattedDate}, ${status}`;

  if (holidayName) {
    label += `, ${holidayName}`;
  }

  return label;
}

// ---- Internal helpers ----

/**
 * Format a Date object to ISO date string YYYY-MM-DD.
 */
function formatDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * Get the nth occurrence of a day-of-week in a given month.
 * @param year - Full year
 * @param month - 0-indexed month (0=Jan, 11=Dec)
 * @param dayOfWeek - 0=Sunday, 6=Saturday
 * @param n - Which occurrence (1=first, 2=second, etc.)
 */
function getNthDayOfMonth(
  year: number,
  month: number,
  dayOfWeek: number,
  n: number
): Date {
  const firstDay = new Date(year, month, 1);
  let firstOccurrence = firstDay.getDay();
  let offset = dayOfWeek - firstOccurrence;
  if (offset < 0) offset += 7;

  const day = 1 + offset + (n - 1) * 7;
  return new Date(year, month, day);
}

/**
 * Get the last occurrence of a day-of-week in a given month.
 */
function getLastDayOfMonth(
  year: number,
  month: number,
  dayOfWeek: number
): Date {
  const lastDay = new Date(year, month + 1, 0); // last day of month
  let offset = lastDay.getDay() - dayOfWeek;
  if (offset < 0) offset += 7;

  return new Date(year, month, lastDay.getDate() - offset);
}

/**
 * Map an OvernightOption to an array of day-of-week numbers (0=Sun, 6=Sat).
 */
function getTargetDaysOfWeek(option: OvernightOption): number[] {
  switch (option) {
    case "sunday":
      return [0];
    case "monday":
      return [1];
    case "tuesday":
      return [2];
    case "wednesday":
      return [3];
    case "thursday":
      return [4];
    case "friday":
      return [5];
    case "saturday":
      return [6];
    case "weekend":
      return [5, 6, 0]; // Fri, Sat, Sun
    case "weekdays":
      return [1, 2, 3, 4]; // Mon-Thu
    default:
      return [];
  }
}

/**
 * Determine if a given date falls in a "matching" week relative to the pattern start date.
 * For "everyOther" frequency, weeks alternate: the week containing the start date is week 0
 * (matching), the next week is week 1 (non-matching), etc.
 *
 * A "week" here is defined as a 7-day period starting from the Monday of the pattern start date's week.
 */
function isMatchingWeek(patternStart: Date, date: Date): boolean {
  // Get the Monday of the pattern start date's week
  const patternMonday = getWeekMonday(patternStart);
  const dateMonday = getWeekMonday(date);

  // Calculate the number of weeks between the two Mondays
  const diffMs = dateMonday.getTime() - patternMonday.getTime();
  const diffWeeks = Math.round(diffMs / (7 * 24 * 60 * 60 * 1000));

  // Even weeks (0, 2, 4, ...) are matching
  return ((diffWeeks % 2) + 2) % 2 === 0;
}

/**
 * Get the Monday of the week containing the given date.
 * (Monday = start of week for pattern calculation purposes)
 */
function getWeekMonday(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay(); // 0=Sun, 6=Sat
  // Shift so Monday=0: (day + 6) % 7
  const diff = (day + 6) % 7;
  d.setDate(d.getDate() - diff);
  return d;
}
